import { useParams } from '@reach/router';
import classNames from 'classnames';
import { Link, graphql } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC } from 'react';
import { MdOutlineErrorOutline } from 'react-icons/md';
import { useQuery } from 'urql';

import { useGetPlayerOverviewPageLink } from '@/bits/links/useLink';
import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
  Table,
  Tippy,
  Value,
} from '@/components';
import { EditIcon, EyeIcon, RefreshIcon } from '@/components/icons';
import { spanCell } from '@/components/Table';
import { useTranslate } from '@/contexts';
import { PlayerRgRiskLevelForm } from '@/forms';
import SubmitRgReviewStatusForm from '@/forms/submit-rg-review-status-form/SubmitRgReviewStatusForm';
import { useRgVersion } from '@/hooks';
import { getRgRiskLevelColor } from '@/utils';
import formatDate from '@/utils/formatter/formatDate';
import { splitStringIntoTwoWords } from '@/utils/split-string-in-two-words';
import {
  RgRiskAssessmentLegacyQuery,
  RgRiskAssessmentQuery,
  RgRiskAssessmentQueryVariables,
} from './__generated__/component';

export const Fragment = graphql`
  fragment SanityPlayerRgRiskAssessmentOverviewBlock on SanityPlayerRgRiskAssessmentOverviewBlock {
    totalRiskAssessment {
      ...SanityLocaleString
    }
    riskLevel {
      ...SanityLocaleString
    }
    calculatedAt {
      ...SanityLocaleString
    }
    lastReviewed {
      ...SanityLocaleString
    }
    overriddenRiskLevel {
      ...SanityLocaleString
    }
    regions {
      ...SanityLocaleString
    }
    brands {
      ...SanityLocaleString
    }
    currentReviewStatus {
      ...SanityLocaleString
    }
    isReviewRequired {
      ...SanityLocaleString
    }
    reviewRequired {
      ...SanityLocaleString
    }
    updateReviewStatus {
      ...SanityLocaleString
    }
    playerGlobalUuid {
      ...SanityLocaleString
    }
  }
`;

const QUERY = gql`
  query RgRiskAssessment($playerGlobalId: ID!) {
    viewer {
      id
      rgCraRiskAssessment: rgCraV2RiskAssessment(
        playerGlobalId: $playerGlobalId
      ) {
        playerGlobalId
        players {
          id
          rawPlayerId
          brand {
            name
          }
        }
        riskLevel
        overriddenRiskLevel
        score
        maxScore
        percentage
        finalScore
        reviewStatusInfo {
          status
          lastReviewed
        }
        reviewTrigger {
          byRisks {
            regionId
            riskId
          }
          byOverallRiskLevel
        }
        calculatedAt
        regions {
          regionName
          score
          maxScore
          percentage
          weight
          finalScore
          risks {
            riskName
            riskLevel
            score
            maxScore
            calculatedAt
            reviewRequired
          }
        }
      }
    }
  }
`;

const LEGACY_QUERY = gql`
  query RgRiskAssessmentLegacy($playerGlobalId: ID!) {
    viewer {
      id
      rgCraRiskAssessment(playerGlobalId: $playerGlobalId) {
        playerGlobalId
        players {
          id
          rawPlayerId
          brand {
            name
          }
        }
        riskLevel
        overriddenRiskLevel
        reviewStatusInfo {
          status
          lastReviewed
        }
        reviewTrigger {
          byRisks {
            regionId
            riskId
          }
          byOverallRiskLevel
        }
        calculatedAt
        regions {
          regionName
          risks {
            riskName
            riskLevel
            calculatedAt
            reviewRequired
          }
        }
      }
    }
  }
`;

const PlayerRgRiskAssessmentOverviewBlock: FC<{
  block: Queries.SanityPlayerRgRiskAssessmentOverviewBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const params = useParams();
  const getPlayerOverviewLink = useGetPlayerOverviewPageLink();
  const { isLegacy } = useRgVersion();

  const [{ data, fetching }, refresh] = useQuery<
    RgRiskAssessmentQuery | RgRiskAssessmentLegacyQuery,
    RgRiskAssessmentQueryVariables
  >({
    query: isLegacy ? LEGACY_QUERY : QUERY,
    variables: {
      playerGlobalId: params.playerGlobalId,
    },
  });

  const riskAssessment = data?.viewer.rgCraRiskAssessment;

  const v2Assessment = !isLegacy
    ? ((riskAssessment as
        | RgRiskAssessmentQuery['viewer']['rgCraRiskAssessment']
        | undefined) ?? null)
    : null;

  const players = riskAssessment?.players || [];

  return (
    <>
      <Card
        size="lg"
        title={
          <div className="flex items-center gap-3">
            {t(block.totalRiskAssessment)}
          </div>
        }
        options={
          <CardOptions>
            <CardOptionsButton
              className="flex"
              onClick={() => refresh({ requestPolicy: 'network-only' })}
            >
              <RefreshIcon />
            </CardOptionsButton>
          </CardOptions>
        }
      >
        <CardBody>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            <Value fetching={fetching} title={t(block.riskLevel)}>
              <div className="flex gap-1 items-center">
                <MdOutlineErrorOutline
                  className={classNames(
                    getRgRiskLevelColor(riskAssessment?.riskLevel),
                  )}
                />
                <span>
                  {splitStringIntoTwoWords(riskAssessment?.riskLevel)}
                </span>
                {riskAssessment?.overriddenRiskLevel && (
                  <span className={'text-red-600'}>*</span>
                )}
                <ControlledModal
                  content={
                    riskAssessment?.riskLevel ? (
                      <PlayerRgRiskLevelForm
                        riskLevel={riskAssessment.riskLevel}
                        playerGlobalId={params?.playerGlobalId}
                      />
                    ) : null
                  }
                >
                  <InlineIconButton>
                    <EditIcon />
                  </InlineIconButton>
                </ControlledModal>
              </div>
            </Value>
            <Value
              fetching={fetching}
              title={t(block.calculatedAt)}
              value={formatDate(riskAssessment?.calculatedAt)}
            />
            <Value fetching={fetching} title={t(block.brands)}>
              <div className="flex items-center">
                {players.length < 3 ? (
                  <span>
                    {players.map((player, i) => {
                      return (
                        <div key={`playerId_${i}`}>
                          <Link
                            key={`playerIdCraLink_${i}`}
                            to={getPlayerOverviewLink(player.id) ?? ''}
                          >
                            {player.brand.name}
                          </Link>
                          {players.length !== i + 1 && <span>, </span>}
                        </div>
                      );
                    })}
                  </span>
                ) : (
                  <>
                    <span className="text-blue-600">{players.length}</span>
                    <ControlledModal
                      content={
                        <Card
                          title="Brand(s)"
                          size="lg"
                          options={
                            <CardOptions>
                              <CardCloseButton />
                            </CardOptions>
                          }
                        >
                          <div className="p-3">
                            <Table
                              headings={['Player ID', 'Brand']}
                              rows={players.map((player, key) => ({
                                key: player.id,
                                data: [
                                  <Link
                                    key={`playerIdLink_${key}`}
                                    className="w-full h-full p-3 block"
                                    to={getPlayerOverviewLink(player.id) ?? ''}
                                  >
                                    {player.rawPlayerId}
                                  </Link>,
                                  player.brand.name,
                                ],
                              }))}
                            />
                          </div>
                        </Card>
                      }
                    >
                      <InlineIconButton>
                        <EyeIcon className="text-gray-400 text-lg" />
                      </InlineIconButton>
                    </ControlledModal>
                  </>
                )}
              </div>
            </Value>
            <Value fetching={fetching} title={t(block.currentReviewStatus)}>
              {riskAssessment?.reviewStatusInfo?.status
                ? riskAssessment.reviewStatusInfo.status
                : ''}

              <ControlledModal
                content={
                  riskAssessment?.reviewStatusInfo.status ? (
                    <SubmitRgReviewStatusForm
                      currentReviewStatus={
                        riskAssessment.reviewStatusInfo.status
                      }
                      playerGlobalId={params?.playerGlobalId}
                      reviewTrigger={riskAssessment.reviewTrigger}
                    />
                  ) : null
                }
              >
                {riskAssessment?.reviewStatusInfo.status ? (
                  <InlineIconButton title={t(block.updateReviewStatus)}>
                    <Tippy content={t(block.updateReviewStatus)}>
                      <div>
                        <EditIcon />
                      </div>
                    </Tippy>
                  </InlineIconButton>
                ) : (
                  <></>
                )}
              </ControlledModal>
            </Value>
            <Value fetching={fetching} title={t(block.lastReviewed)}>
              {formatDate(riskAssessment?.reviewStatusInfo?.lastReviewed)}
            </Value>
            <Value
              fetching={fetching}
              title={t(block.playerGlobalUuid)}
              value={riskAssessment?.playerGlobalId}
            />
            {!isLegacy && (
              <>
                <Value
                  fetching={fetching}
                  title="Score"
                  value={
                    v2Assessment?.score != null ? `${v2Assessment.score}` : ''
                  }
                />
                <Value
                  fetching={fetching}
                  title="Max Score"
                  value={
                    v2Assessment?.maxScore != null
                      ? `${v2Assessment.maxScore}`
                      : ''
                  }
                />
                <Value
                  fetching={fetching}
                  title="Percentage"
                  value={
                    v2Assessment?.percentage != null
                      ? `${v2Assessment.percentage.toFixed(2)}%`
                      : ''
                  }
                />
                <Value
                  fetching={fetching}
                  title="Final Score"
                  value={
                    v2Assessment?.finalScore != null
                      ? `${v2Assessment.finalScore.toFixed(2)}%`
                      : ''
                  }
                />
              </>
            )}
          </div>
        </CardBody>
      </Card>
      <Card size="lg" title={t(block.regions)}>
        <div className="p-3">
          <Table
            tableLayout="auto"
            headings={[
              'Risk Name',
              t(block.riskLevel),
              ...(!isLegacy ? ['Score', 'Max Score'] : []),
              t(block.calculatedAt),
              t(block.isReviewRequired),
            ]}
            rows={
              riskAssessment?.regions.flatMap((region, r) => {
                const v2Region = v2Assessment?.regions[r];
                const colSpan = isLegacy ? 4 : 6;

                const groupHeaderRow = {
                  key: `region_${r}`,
                  className: 'bg-gray-100 dark:bg-gray-700',
                  data: [
                    spanCell(
                      isLegacy
                        ? region.regionName
                        : `${region.regionName}${v2Region?.weight != null ? ` (Weight: ${v2Region.weight.toFixed(2)}%)` : ''}`,
                      {
                        colSpan,
                        className:
                          'font-semibold text-gray-900 dark:text-gray-100',
                      },
                    ),
                  ],
                };

                const riskRows = region.risks.map((risk, i) => {
                  const v2Risk = v2Region?.risks[i];
                  return {
                    key: risk.riskName,
                    data: [
                      risk.riskName,
                      risk.riskLevel,
                      ...(!isLegacy ? [v2Risk?.score, v2Risk?.maxScore] : []),
                      formatDate(risk.calculatedAt),
                      risk.reviewRequired ? (
                        <span>{t(block.reviewRequired)}</span>
                      ) : null,
                    ],
                  };
                });

                const summaryRow =
                  !isLegacy && v2Region
                    ? [
                        {
                          key: `summary_${r}`,
                          className: 'text-xs font-medium',
                          cellClassName:
                            'border-t-2 border-gray-400 dark:border-gray-500',
                          data: [
                            spanCell(null, { colSpan: 2 }),
                            v2Region.percentage != null
                              ? `Percentage: ${v2Region.percentage.toFixed(2)}%`
                              : '-',
                            `Final Score: ${v2Region.finalScore != null ? `${v2Region.finalScore.toFixed(2)}%` : '-'}`,
                            `Score: ${v2Region.score ?? '-'}`,
                            `Max: ${v2Region.maxScore ?? '-'}`,
                          ],
                        },
                      ]
                    : [];

                return [groupHeaderRow, ...riskRows, ...summaryRow];
              }) ?? []
            }
          />
        </div>
      </Card>
    </>
  );
};

export default PlayerRgRiskAssessmentOverviewBlock;
